import { RawLinkButton } from '@/components/Button';
import { Card } from '@/components/Card';
import { Flex, Grid } from '@/components/Grid';
import { Heading } from '@/components/Heading';
import { Html } from '@/components/Html';
import { Illustration } from '@/components/Illustration';
import { Section } from '@/components/Section';
import {
  BlockType,
  IllustrationBlockType,
  PageType,
  TextBlockType,
  getPage,
} from '@/resource/page';
import { staticProps } from '@/util/staticProps';
import { tryCatch } from '@/util/tryCatch';
import * as validators from '@/util/validators';
import { GetStaticPaths } from 'next';
import { NextSeo } from 'next-seo';
import z from 'zod';

type Props = {
  page: PageType;
};

function IllustrationBlock({
  block: { illustration, title, text, cta_label, cta_path },
}: {
  block: IllustrationBlockType;
}) {
  return (
    <Grid
      columns={illustration ? { md: [4, 8], lg: [3, 7] } : { md: [8], lg: [7] }}
      offsets={illustration ? { lg: 1 } : { md: 4, lg: 4 }}
    >
      {illustration && (
        <Card>
          <Flex justify="center">
            <Illustration illustration={illustration} />
          </Flex>
        </Card>
      )}

      <Flex dir="column" gap={2}>
        <Heading level={2} visualLevel={3}>
          {title}
        </Heading>

        <Html html={text} />

        {cta_path && cta_label && (
          <Flex justify="end">
            <RawLinkButton theme="secondary" href={cta_path}>
              {cta_label}
            </RawLinkButton>
          </Flex>
        )}
      </Flex>
    </Grid>
  );
}

function TextBlock({ block }: { block: TextBlockType }) {
  return (
    <Grid columns={{ md: [12], lg: [10] }} offsets={{ lg: 1 }}>
      <Html html={block.text} />
    </Grid>
  );
}

function Block({ block }: { block: BlockType }) {
  switch (block.external_table) {
    case 'block_illustrations':
      return <IllustrationBlock block={block.block.data} />;
    case 'block_texts':
      return <TextBlock block={block.block.data} />;
  }

  return null;
}

export default function Page({ page }: Props) {
  return (
    <>
      <NextSeo title={page.title} />
      <Section>
        <Flex dir="column" gap={4}>
          <Grid columns={{ lg: [10] }} offsets={{ lg: 1 }}>
            <Heading>{page.title}</Heading>
          </Grid>

          {page.story_builder?.data.map((block) => (
            <Block
              block={block}
              key={`${block.external_table}_${block.block.data.id}`}
            />
          ))}
        </Flex>
      </Section>
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps = staticProps
  .params(
    z.object({
      slug: validators.slug({ min: 1, max: 128 }),
    })
  )
  .resolve<Props>(async ({ slug }) => {
    const page = await tryCatch(getPage({ slug }));

    if (!page) {
      return {
        notFound: true,
        revalidate: 10,
      };
    }

    return {
      props: {
        page,
      },
      revalidate: 30,
    };
  });
